import {
  Component,
  computed,
  EventEmitter,
  inject,
  Output,
} from '@angular/core';
import { TravelBoardService } from '../../services/travel-board.service';
import { SelectButtonModule } from 'primeng/selectbutton';
import { LucideAngularModule } from 'lucide-angular';
import { TranslocoPipe } from '@jsverse/transloco';
import { InputIconModule } from 'primeng/inputicon';
import { FormsModule } from '@angular/forms';
import { IconFieldModule } from 'primeng/iconfield';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';

@Component({
  selector: 'app-filter-trips',
  imports: [
    SelectButtonModule,
    LucideAngularModule,
    TranslocoPipe,
    InputIconModule,
    IconFieldModule,
    ButtonModule,
    FormsModule,
    InputTextModule,
  ],
  templateUrl: './filter-trips.component.html',
  styleUrl: './filter-trips.component.scss',
})
export class FilterTripsComponent {
  travelBoardService = inject(TravelBoardService);

  @Output() searchChange = new EventEmitter<string>();
  @Output() filterChange = new EventEmitter<'all' | 'draft' | 'notDraft'>();

  // Direct access to service signals
  get searchText(): string {
    return this.travelBoardService.searchText();
  }
  set searchText(value: string) {
    this.travelBoardService.searchText.set(value);
    this.travelBoardService.resetPagination();
    this.searchChange.emit(value);
  }

  get isDraftFilter(): 'all' | 'draft' | 'notDraft' {
    return this.travelBoardService.isDraftFilter();
  }
  set isDraftFilter(value: 'all' | 'draft' | 'notDraft') {
    this.travelBoardService.isDraftFilter.set(value);
    this.travelBoardService.resetPagination();
    this.filterChange.emit(value);
  }

  // Multi-select quick filters, rendered with the same SelectButton style as
  // the draft/calculated status filter. Each value toggles independently.
  // Derived as a computed so the reference stays stable across change-detection
  // cycles (a fresh array each read would trigger
  // ExpressionChangedAfterItHasBeenCheckedError); it recomputes only when the
  // underlying filter signals change. Bound one-way ([ngModel] + ngModelChange).
  readonly activeQuickFilters = computed<string[]>(() => {
    const values: string[] = [];
    if (this.travelBoardService.showOnlyNeedsVerification()) {
      values.push('needsVerification');
    }
    if (this.travelBoardService.showOnlyWithErrors()) {
      values.push('withErrors');
    }
    return values;
  });

  onQuickFiltersChange(values: string[]): void {
    this.travelBoardService.showOnlyNeedsVerification.set(
      values.includes('needsVerification'),
    );
    this.travelBoardService.showOnlyWithErrors.set(values.includes('withErrors'));
    this.travelBoardService.resetPagination();
  }

  clearSearch(): void {
    this.travelBoardService.searchText.set('');
    this.travelBoardService.resetPagination();
    this.searchChange.emit('');
  }

  get hasActiveFilters(): boolean {
    return (
      this.travelBoardService.searchText() !== '' ||
      this.travelBoardService.isDraftFilter() !== 'all' ||
      this.travelBoardService.showOnlyNeedsVerification() ||
      this.travelBoardService.showOnlyWithErrors()
    );
  }

  get statusCounts(): Record<string, number> {
    const data = this.travelBoardService.data();
    return {
      all: data.length,
      draft: data.filter((t) => t.isDraft).length,
      notDraft: data.filter((t) => !t.isDraft).length,
    };
  }

  get quickFilterCounts(): Record<string, number> {
    const data = this.travelBoardService.data();
    return {
      needsVerification: data.filter((t) => t.needsVerification).length,
      withErrors: data.filter((t) => t.hasValidationErrors).length,
    };
  }

  resetFilters(): void {
    this.travelBoardService.searchText.set('');
    this.travelBoardService.isDraftFilter.set('all');
    this.travelBoardService.showOnlyNeedsVerification.set(false);
    this.travelBoardService.showOnlyWithErrors.set(false);
    this.travelBoardService.resetPagination();
    this.searchChange.emit('');
    this.filterChange.emit('all');
  }
}
